import { useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import { useFormik } from 'formik';
import moment from 'moment/min/moment-with-locales';
import * as Yup from 'yup';
import { Trans, useTranslation } from 'react-i18next';
import { CONTACT_EMAIL, OTP_LENGTH } from '@/helpers/constants';
import { APP_LINKS } from '@/helpers/links';
import { MyLink } from '@/components/MyLink';
import { useReCaptcha } from '@/providers/recaptcha';
import { useCountdownFromNow } from '@scramble/hooks';
import { InlineAlert, Snackbar, VerificationCodeInput } from '@scramble/ui';
import { useGetTwoFactorOtpStatus } from '@/query/queries/auth/useGetTwoFactorOtpStatus';
import { useResendTwoFactorOtpCode } from '@/query/queries/auth/useResendTwoFactorOtpCode';
import { useSubmitTwoFactorOtpCode } from '@/query/queries/auth/useSubmitTwoFactorOtpCode';
import { clearTempUserData } from '@/pages/AuthPage/utils';
import s from './index.module.scss';

const VALIDATION_SCHEMA = t =>
  Yup.object({
    code: Yup.string().min(
      OTP_LENGTH,
      t('validation:verification_code_is_invalid'),
    ),
  });

const SMS_LIMIT = 3;

const CHANNELS = {
  whatsapp: 'whatsapp',
  sms: 'sms',
  call: 'call',
};

export const StepPhoneOtp = ({ onSuccess  }) => {
  const { t } = useTranslation();
  const { token: captchaToken, loaded, getCaptchaToken } = useReCaptcha();
  const [success, setSuccess] = useState(false);
  const mounted = useRef(false);

  const onTokenExpired = () => {
    clearTempUserData();
    window.location.reload();
  };

  const { data: OtpStatus } = useGetTwoFactorOtpStatus({ onTokenExpired });
  const { mutateAsync: resendCode } = useResendTwoFactorOtpCode({
    onTokenExpired,
  });
  const onCaptchaExpired = () => {
    Snackbar({ type: 'warning', text: t('auth:otp.session_expired') });
  };

  const { mutateAsync: submitCode } = useSubmitTwoFactorOtpCode({
    onTokenExpired,
    onCaptchaExpired,
  });

  const [expireTime, setExpireTime] = useState(null);

  const timeLeft = useCountdownFromNow(expireTime, () => {
    mounted.current && setExpireTime(null);
  });

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const lastCreated = OtpStatus?.last_created_at;
    if (lastCreated) {
      const currentTime = moment();
      const expireTime = moment(OtpStatus.last_created_at).add(
        OtpStatus.count >= SMS_LIMIT ? 10 : 1,
        'minutes',
      );

      mounted.current && currentTime.isBefore(expireTime)
        ? setExpireTime(expireTime.toISOString())
        : setExpireTime(null);
    } else {
      mounted.current && setExpireTime(null);
    }
  }, [OtpStatus]);

  const onSubmit = async ({ code }, { setErrors }) => {
    if (!loaded) return;
    const recaptchaToken = captchaToken || (await getCaptchaToken());
    if (!recaptchaToken) return;
    const res = await submitCode({ code, recaptcha_token: recaptchaToken });

    if (res?.success) {
      setSuccess(true);
      setTimeout(() => {
        onSuccess(res.data);
      }, 500); // use delay to show visual green effects
    }

    if (res?.captchaExpired) setFieldValue('code', '');

    if (res?.error) {
      setErrors({ code: res.error });
    }
  };

  const { values, errors, touched, setFieldValue, handleSubmit } = useFormik({
    initialValues: { code: '' },
    validationSchema: VALIDATION_SCHEMA(t),
    onSubmit,
  });

  useEffect(() => {
    if (values.code.length === OTP_LENGTH && loaded) {
      handleSubmit();
    }
  }, [values.code, handleSubmit, loaded]);

  const onResend = async () => {
    if (!loaded) return;
    const recaptchaToken = captchaToken || (await getCaptchaToken());
    if (!recaptchaToken) return;
    await setFieldValue('code', '');
    await resendCode({ recaptcha_token: recaptchaToken });
  };

  const hasReached = OtpStatus?.count >= SMS_LIMIT;
  const canResend = !!(OtpStatus && !hasReached && !expireTime);
  const lastFourNumbers = OtpStatus?.phone_number.substring(OtpStatus.phone_number.length - 4) || '';

  return (
    <form className={classnames(s.wrapper)} onSubmit={handleSubmit}>
      <div className={s.content}>
        <div className={s.header}>
          <h1 className="f-headline-sm f-500">
            {t('auth:step_phone_otp.title')}
          </h1>

          {OtpStatus && (
            <>
              {OtpStatus.next_channel === CHANNELS.whatsapp && (
                <div className="f-body-md">
                  <Trans
                    ns="auth"
                    i18nKey="auth:step_phone_otp.sent_sms"
                    components={{ Span: <span className="f-600" /> }}
                    values={{ lastFourNumbers }}
                  />
                </div>
              )}

              {OtpStatus.next_channel === CHANNELS.call && (
                <div className="f-body-md">
                  <Trans
                    ns="auth"
                    i18nKey="auth:step_phone_otp.sent_whatsapp"
                    components={{ Span: <span className="f-600" /> }}
                    values={{ lastFourNumbers }}
                  />
                </div>
              )}

              {OtpStatus.next_channel === CHANNELS.sms && (
                <div className="f-body-md">
                  <Trans
                    ns="auth"
                    i18nKey="auth:step_phone_otp.sent_call"
                    components={{ Span: <span className="f-600" /> }}
                    values={{ lastFourNumbers }}
                  />
                </div>
              )}
            </>
          )}
        </div>

        <div className={s.input}>
          <VerificationCodeInput
            label={t('auth:otp.verification_code')}
            value={values.code}
            onChange={value => setFieldValue('code', value)}
            error={errors.code && touched.code}
            success={success}
            disabled={hasReached && !timeLeft}
          />
          {errors.code && touched.code && (
            <InlineAlert
              type="danger"
              className={s.input_error}
              title={errors.code}
            />
          )}
        </div>

        {OtpStatus && (
          <div className={s.message}>
            {timeLeft ? (
              hasReached ? (
                <div className={s.expires_in}>
                  <Trans
                    ns="auth"
                    i18nKey="auth:otp.code_expires"
                    components={{
                      Nowrap: <span className="f-500 nowrap" />,
                    }}
                    values={{ time: timeLeft }}
                  />
                </div>
              ) : (
                <div className={s.resend_code_in}>
                  <Trans
                    ns="auth"
                    i18nKey="auth:otp.resend_code_in"
                    components={{
                      Span: <span className="f-500" />,
                      Nowrap: <span className="nowrap" />,
                    }}
                    values={{ time: timeLeft }}
                  />
                </div>
              )
            ) : (
              canResend && (
                <button
                  type="button"
                  onClick={onResend}
                  className={s.btn_resend}
                >
                  {OtpStatus.next_channel === CHANNELS.whatsapp && (
                    <span>{t('auth:otp.resend_whatsapp')}</span>
                  )}
                  {OtpStatus.next_channel === CHANNELS.call && (
                    <span>{t('auth:otp.resend_call')}</span>
                  )}
                  {OtpStatus.next_channel === CHANNELS.sms && (
                    <span>{t('auth:otp.request_new_code')}</span>
                  )}
                </button>
              )
            )}
          </div>
        )}

        {hasReached && (
          <>
            <div className={s.divider} />
            <div className={classnames(s.trouble, 'f-body-sm')}>
              <Trans
                ns="auth"
                i18nKey="auth:otp.trouble_contact_support"
                values={{ email: CONTACT_EMAIL }}
                components={{
                  LinkSupport: (
                    <MyLink
                      className="f-500"
                      href={APP_LINKS.email}
                      isExternal
                    />
                  ),
                }}
              />
            </div>
          </>
        )}
      </div>
    </form>
  );
};
